import { useEffect } from 'react'
import { INPUT_PHONE_KEY } from '@/components/input-phone-storage'

/**
 * «Телефон» в новой теме принимает любой номер. В поле можно ввести только
 * цифры, «+», пробел, скобки и дефис; номер проходит, если цифр в нём от 10
 * до 15. Ни префикс (7/8/9), ни страну не проверяем. То же правило — у
 * «Номера для счёта».
 */
export function keepPhoneChars(value: string) {
  return value.replace(/[^\d+ ()-]/g, '')
}

export function isPhoneLike(value: string) {
  const digits = value.replace(/\D/g, '').length
  return digits >= 10 && digits <= 15
}

/**
 * Недопустимые символы в «Телефоне» отклоняются до ввода (beforeinput), а не
 * фильтром после него. Фильтр после ввода ставил значение программно, и поле
 * возвращало каретку на место лишь в следующем кадре: при быстром наборе
 * следующие символы приходили раньше, и цифры вставали не по порядку
 * («abc 8 (701) 234-56-78» → «) 234-56-78 8 (701»). Отклонённый до ввода
 * символ значение не меняет вовсе, каретка остаётся где была; из вставленной
 * строки в поле попадает только допустимая часть.
 */
function onBeforeInput(event: InputEvent) {
  const target = event.target
  if (!(target instanceof HTMLInputElement) || target.dataset.inputId !== INPUT_PHONE_KEY) {
    return
  }
  if (!event.inputType.startsWith('insert')) {
    return
  }
  const data = event.data ?? event.dataTransfer?.getData('text/plain') ?? null
  if (data === null) {
    return
  }
  const start = target.selectionStart ?? target.value.length
  const end = target.selectionEnd ?? start
  // пробел в начале номера не нужен: после отброшенных букв из
  // «abc 8 (701) 234-56-78» в поле оставалось « 8 (701) 234-56-78»
  const atStart = target.value.slice(0, start).trim() === ''
  const kept = atStart ? keepPhoneChars(data).replace(/^\s+/, '') : keepPhoneChars(data)
  if (kept === data) {
    return
  }
  event.preventDefault()
  if (kept === '') {
    return
  }
  target.setRangeText(kept, start, end, 'end')
  // React узнаёт о новом значении из события ввода, как при обычном наборе
  target.dispatchEvent(new Event('input', { bubbles: true }))
}

const listeners = { count: 0 }

/** Включает правило ввода для «Телефона», пока смонтирован вызвавший компонент. */
export function useRejectNonPhoneChars() {
  useEffect(() => {
    if (listeners.count === 0) {
      document.addEventListener('beforeinput', onBeforeInput, true)
    }
    // eslint-disable-next-line immutable/no-mutation
    listeners.count += 1
    return () => {
      // eslint-disable-next-line immutable/no-mutation
      listeners.count -= 1
      if (listeners.count === 0) {
        document.removeEventListener('beforeinput', onBeforeInput, true)
      }
    }
  }, [])
}
